/**
 * TODO: [HALO-MIGRATION] Delete this entire file after migration is complete
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Button,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { Feedback } from '@fashionnovateam/nova-ui-toolkit';

const HALO_CREATOR_CENTER_URL = 'https://creator.fashionnova.com';
const MODAL_DISMISSED_KEY = 'halo_migration_modal_dismissed';

type MigrationType = 'influencer' | 'agency';

interface HaloMigrationMobileModalProps {
  migrationType: MigrationType;
}

const CONTENT = {
  influencer: {
    title: 'YOUR NEW CREATOR CENTER HAS ARRIVED.',
    subtitle:
      'Experience a more intuitive way to manage your partnership. Your upgraded home for growth is here.',
    mobileImage: '/assets/migration/creator_modal_mobile.png',
    imageAlt: 'Creator Center Preview',
  },
  agency: {
    title: 'YOUR NEW AGENCY PORTAL HAS ARRIVED.',
    subtitle:
      'Experience a more intuitive way to manage your influencers and agents. Your upgraded home for growth is here.',
    mobileImage: '/assets/migration/agency_modal_mobile.png',
    imageAlt: 'Agency Portal Preview',
  },
};

function HaloMigrationMobileModal({
  migrationType,
}: HaloMigrationMobileModalProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem(MODAL_DISMISSED_KEY);
    if (!dismissed) {
      setOpen(true);
    }
  }, []);

  const handleClose = () => {
    sessionStorage.setItem(MODAL_DISMISSED_KEY, 'true');
    setOpen(false);
  };

  const handleGoToAccount = () => {
    sessionStorage.setItem(MODAL_DISMISSED_KEY, 'true');
    window.open(HALO_CREATOR_CENTER_URL, '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  const content = CONTENT[migrationType];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: '#fff',
        },
      }}
    >
      {/* Top bar with close button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          p: '12px',
          pr: '16px',
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: 'black',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'grey.200',
            width: '44px',
            height: '44px',
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: 0,
          position: 'relative',
          overflowY: 'auto',
        }}
      >
        {/* Content */}
        <Box
          sx={{
            px: '24px',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1,
            position: 'relative',
            minHeight: 'calc(100vh - 68px)',
          }}
        >
          {/* Bottom Image - positioned at bottom, overlapping button area on tall screens */}
          <Box
            component="img"
            src={content.mobileImage}
            alt={content.imageAlt}
            sx={{
              width: '100%',
              maxWidth: '400px',
              objectFit: 'contain',
              position: 'absolute',
              bottom: '0',
              right: migrationType === 'influencer' ? '50%' : '0',
              transform:
                migrationType === 'influencer' ? 'translateX(50%)' : 'none',
            }}
          />

          {/* Header Logo */}
          <Box
            component="img"
            src="/assets/migration/logo-light.png"
            alt="FashionNova Creators"
            sx={{
              width: '180px',
              mb: '24px',
            }}
          />

          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: '28px',
              lineHeight: '32px',
              color: 'black',
              mb: '16px',
            }}
          >
            {content.title}
          </Typography>

          {/* Subtitle */}
          <Typography
            sx={{
              fontSize: '18px',
              color: 'black',
              mb: '24px',
              lineHeight: '24px',
            }}
          >
            {content.subtitle}
          </Typography>

          {/* Checklist */}
          <Box sx={{ mb: '24px' }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}
            >
              <CheckIcon sx={{ fontSize: 20, color: 'black' }} />
              <Typography sx={{ fontSize: '14px', color: 'black' }}>
                Sign in with the same email as this account
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CheckIcon sx={{ fontSize: 20, color: 'black' }} />
              <Typography sx={{ fontSize: '14px', color: 'black' }}>
                Continue to view all of your payment details
              </Typography>
            </Box>
          </Box>

          {/* Info box */}
          <Feedback
            className="p-[12px] mb-[24px]"
            type="info"
            title="Any edits to your profile or payment details must be made in the new portal."
          />

          {/* CTA Button */}
          <Button
            variant="contained"
            onClick={handleGoToAccount}
            sx={{
              backgroundColor: '#000',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '20px',
              px: 4,
              py: 1.5,
              borderRadius: '90px',
              alignSelf: 'flex-start',
              marginBottom: '252px',
            }}
          >
            Go to your account
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default HaloMigrationMobileModal;
